import type { AuthTokens } from '@/types/auth';

/**
 * Storage keys for auth tokens
 */
export const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: 'cdn_access_token',
  REFRESH_TOKEN: 'cdn_refresh_token',
  EXPIRES_AT: 'cdn_expires_at',
} as const;

/**
 * Consider a token expired this many milliseconds before it actually expires
 */
export const TOKEN_EXPIRY_SKEW_MS = 60_000;

/**
 * Token persistence abstraction used by the API client and the auth context
 */
export interface TokenStorage {
  getTokens(): AuthTokens | null;
  setTokens(tokens: AuthTokens): void;
  clearTokens(): void;
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  /**
   * True if there is no token or it expires within TOKEN_EXPIRY_SKEW_MS
   */
  isTokenExpired(): boolean;
  /**
   * Update only the access token (after refresh)
   */
  updateAccessToken(accessToken: string, expiresInSeconds: number): void;
}

/**
 * Create a token storage backed by a Web Storage implementation.
 *
 * @param getStorage - lazily resolves the storage (defaults to localStorage)
 * @param now - clock, injectable for tests
 */
export function createTokenStorage(
  getStorage: () => Storage = () => window.localStorage,
  now: () => number = () => Date.now()
): TokenStorage {
  const storage = () => getStorage();

  return {
    getTokens() {
      const accessToken = storage().getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = storage().getItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
      const expiresAt = storage().getItem(TOKEN_STORAGE_KEYS.EXPIRES_AT);

      if (!accessToken || !refreshToken || !expiresAt) {
        return null;
      }

      const parsedExpiresAt = parseInt(expiresAt, 10);
      if (Number.isNaN(parsedExpiresAt)) {
        return null;
      }

      return { accessToken, refreshToken, expiresAt: parsedExpiresAt };
    },

    setTokens(tokens) {
      storage().setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
      storage().setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
      storage().setItem(TOKEN_STORAGE_KEYS.EXPIRES_AT, tokens.expiresAt.toString());
    },

    clearTokens() {
      storage().removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
      storage().removeItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
      storage().removeItem(TOKEN_STORAGE_KEYS.EXPIRES_AT);
    },

    getAccessToken() {
      return storage().getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
    },

    getRefreshToken() {
      return storage().getItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
    },

    isTokenExpired() {
      const expiresAt = parseInt(storage().getItem(TOKEN_STORAGE_KEYS.EXPIRES_AT) ?? '', 10);
      if (Number.isNaN(expiresAt)) {
        return true;
      }
      return now() >= expiresAt - TOKEN_EXPIRY_SKEW_MS;
    },

    updateAccessToken(accessToken, expiresInSeconds) {
      storage().setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      storage().setItem(TOKEN_STORAGE_KEYS.EXPIRES_AT, (now() + expiresInSeconds * 1000).toString());
    },
  };
}

/**
 * Default token storage (browser localStorage)
 */
export const tokenStorage: TokenStorage = createTokenStorage();
